"use client";

import * as React from "react";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

type CalendarProps = {
  mode?: "single";
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
  disabled?: (date: Date) => boolean;
  fromYear?: number;
  initialFocus?: boolean;
  toYear?: number;
  className?: string;
};

export function Calendar({
  mode: _mode,
  selected,
  onSelect,
  disabled,
  fromYear = 1900,
  initialFocus: _initialFocus,
  toYear = new Date().getFullYear(),
  className,
}: CalendarProps) {
  void _mode;
  void _initialFocus;
  const [visibleMonth, setVisibleMonth] = React.useState(() => {
    const base = selected ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const days = React.useMemo(() => {
    const start = startOfWeek(startOfMonth(visibleMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(visibleMonth), { weekStartsOn: 0 });
    const allDays = eachDayOfInterval({ start, end });
    const weeks: Date[][] = [];
    for (let index = 0; index < allDays.length; index += 7) {
      weeks.push(allDays.slice(index, index + 7));
    }
    return weeks;
  }, [visibleMonth]);

  const handleSelect = (date: Date) => {
    if (disabled?.(date)) {
      return;
    }
    onSelect?.(date);
  };

  const handleMonthChange = (offset: number) => {
    setVisibleMonth((prev) => {
      const next = addMonths(prev, offset);
      if (next.getFullYear() < fromYear) {
        return new Date(fromYear, 0, 1);
      }
      if (next.getFullYear() > toYear) {
        return new Date(toYear, 11, 1);
      }
      return new Date(next.getFullYear(), next.getMonth(), 1);
    });
  };

  const handleMonthDropdown = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const month = Number(event.target.value);
    setVisibleMonth((prev) => new Date(prev.getFullYear(), month, 1));
  };

  const handleYearDropdown = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const year = Number(event.target.value);
    const clampedYear = Math.min(Math.max(fromYear, year), toYear);
    setVisibleMonth((prev) => new Date(clampedYear, prev.getMonth(), 1));
  };

  React.useEffect(() => {
    if (!selected) {
      return;
    }
    setVisibleMonth((prev) => {
      if (prev.getFullYear() === selected.getFullYear() && prev.getMonth() === selected.getMonth()) {
        return prev;
      }
      return new Date(selected.getFullYear(), selected.getMonth(), 1);
    });
  }, [selected]);

  const years = React.useMemo(() => {
    const values: number[] = [];
    for (let year = toYear; year >= fromYear; year -= 1) {
      values.push(year);
    }
    return values;
  }, [fromYear, toYear]);

  const firstMonth = new Date(fromYear, 0, 1);
  const lastMonth = new Date(toYear, 11, 1);
  const isAtStart =
    visibleMonth.getFullYear() === firstMonth.getFullYear() && visibleMonth.getMonth() === firstMonth.getMonth();
  const isAtEnd =
    visibleMonth.getFullYear() === lastMonth.getFullYear() && visibleMonth.getMonth() === lastMonth.getMonth();

  return (
    <div className={cn("p-3", className)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="inline-flex size-7 items-center justify-center rounded-md border bg-background text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => handleMonthChange(-1)}
            disabled={isAtStart}
            aria-label="Previous month"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            className="inline-flex size-7 items-center justify-center rounded-md border bg-background text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => handleMonthChange(1)}
            disabled={isAtEnd}
            aria-label="Next month"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="h-8 rounded-md border bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            value={visibleMonth.getMonth()}
            onChange={handleMonthDropdown}
          >
            {Array.from({ length: 12 }).map((_, month) => (
              <option key={month} value={month}>
                {format(new Date(visibleMonth.getFullYear(), month, 1), "LLLL")}
              </option>
            ))}
          </select>
          <select
            className="h-8 rounded-md border bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            value={visibleMonth.getFullYear()}
            onChange={handleYearDropdown}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-muted-foreground">
            {Array.from({ length: 7 }).map((_, index) => (
              <th key={index} className="py-1 text-center font-normal">
                {format(addDays(startOfWeek(new Date(2023, 0, 1), { weekStartsOn: 0 }), index), "EEE").slice(0, 2)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-sm">
          {days.map((week, weekIndex) => (
            <tr key={weekIndex} className="">
              {week.map((day) => {
                const isOutside = !isSameMonth(day, visibleMonth);
                const isSelected = selected && isSameDay(day, selected);
                const isDisabled = disabled?.(day);

                return (
                  <td key={day.toISOString()} className="p-1 text-center">
                    <button
                      type="button"
                      onClick={() => handleSelect(day)}
                      disabled={isDisabled}
                      className={cn(
                        "inline-flex size-9 items-center justify-center rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "bg-transparent text-foreground hover:bg-muted",
                        isOutside && "text-muted-foreground opacity-50",
                        isDisabled && "cursor-not-allowed text-muted-foreground opacity-50",
                      )}
                    >
                      {format(day, "d")}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

Calendar.displayName = "Calendar";
